'use client';

import React, { useState, useEffect } from 'react';
import { Sparkles, Wand2, Image as ImageIcon } from 'lucide-react';
import { useImageGeneration } from '@/lib/hooks/useImageGeneration';
import { toast } from 'sonner';
import type { FlyerState } from './FlyerBuilderContent';

interface Template {
  id: string;
  name: string;
  platform: string;
  width: number;
  height: number;
  bgColor: string;
}

interface AIGenerationPanelProps {
  templates: Template[];
  onTemplateSelect: (id: string) => void;
  onAIGenerated: (base64: string) => void;
  flyerState: FlyerState;
  setFlyerState: React.Dispatch<React.SetStateAction<FlyerState>>;
}

const SIZE_OPTIONS = [
  { label: 'Square (1:1)', value: '1024x1024', desc: 'Instagram Post' },
  { label: 'Landscape (16:9)', value: '1792x1024', desc: 'Facebook Post' },
  { label: 'Portrait (9:16)', value: '1024x1792', desc: 'Instagram Story' },
];

const PROMPT_SUGGESTIONS = [
  'A vibrant summer sale flyer for a fashion brand with bold colors and modern typography',
  'A professional real estate open house flyer with luxury home imagery and elegant design',
  'A colorful food promotion flyer for a restaurant with appetizing visuals',
  'A tech product launch announcement with futuristic design and gradient background',
  'A fitness gym promotion flyer with energetic colors and motivational text',
];

export default function AIGenerationPanel({
  templates,
  onTemplateSelect,
  onAIGenerated,
}: AIGenerationPanelProps) {
  const [prompt, setPrompt] = useState('');
  const [selectedSize, setSelectedSize] = useState('1024x1024');

  const { image, isLoading, error, generate } = useImageGeneration(
    'GEMINI',
    'gemini/gemini-2.5-flash-image'
  );

  useEffect(() => {
    if (error) toast.error(error.message || 'Failed to generate image');
  }, [error]);

  useEffect(() => {
    if (image?.data?.[0]?.b64_json) {
      toast.success('Flyer generated! Opening in canvas editor...');
      setTimeout(() => onAIGenerated(image.data[0].b64_json), 800);
    }
  }, [image]);

  const handleGenerate = () => {
    if (!prompt.trim() || isLoading) return;
    const enhancedPrompt = `Create a professional marketing flyer: ${prompt}. High quality, print-ready, vibrant colors, clear typography, suitable for social media advertising.`;
    generate(enhancedPrompt, { size: selectedSize, n: 1 });
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Left: AI Generation */}
      <div className="xl:col-span-2 space-y-5">
        {/* AI Prompt Card */}
        <div className="card-base p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Sparkles size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">AI Flyer Generator</h2>
              <p className="text-xs text-muted-foreground">Powered by Gemini</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="label-base">Describe your flyer</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. A summer sale flyer for a clothing brand with bright colors, 50% off promotion, and modern design..."
                rows={4}
                className="input-base resize-none"
                disabled={isLoading}
              />
              <p className="helper-text">Be specific about colors, style, content, and target audience for best results.</p>
            </div>

            {/* Size Selection */}
            <div>
              <label className="label-base">Canvas Size</label>
              <div className="grid grid-cols-3 gap-2">
                {SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedSize(opt.value)}
                    className={`p-3 rounded-lg border text-left transition-all duration-150 ${
                      selectedSize === opt.value
                        ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/40 text-foreground'
                    }`}
                  >
                    <p className="text-xs font-semibold">{opt.label}</p>
                    <p className="text-[11px] text-muted-foreground">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={!prompt.trim() || isLoading}
              className="btn-primary w-full justify-center py-3"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generating Flyer...
                </>
              ) : (
                <>
                  <Wand2 size={16} />
                  Generate Flyer with AI
                </>
              )}
            </button>
          </div>
        </div>

        {/* Prompt Suggestions */}
        <div className="card-base p-5">
          <h3 className="text-sm font-semibold text-foreground mb-3">Prompt Suggestions</h3>
          <div className="space-y-2">
            {PROMPT_SUGGESTIONS.map((suggestion, i) => (
              <button
                key={i}
                onClick={() => setPrompt(suggestion)}
                className="w-full text-left px-3 py-2.5 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-150 text-sm text-muted-foreground hover:text-foreground"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Templates */}
      <div className="space-y-4">
        <div className="card-base p-5">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Pre-built Templates</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Start with a template and customize in the canvas editor.</p>
          <div className="space-y-2">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                onClick={() => onTemplateSelect(tpl.id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-primary/5 transition-all duration-150 text-left group"
              >
                {/* Color preview */}
                <div
                  className="w-10 h-10 rounded-lg shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: tpl.bgColor }}
                >
                  {tpl.platform === 'Facebook' && <span className="text-white text-[10px] font-bold">FB</span>}
                  {tpl.platform === 'Instagram' && <span className="text-white text-[10px] font-bold">IG</span>}
                  {tpl.platform === 'Custom' && <ImageIcon size={14} className="text-white opacity-70" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{tpl.name}</p>
                  <p className="text-xs text-muted-foreground">{tpl.width} × {tpl.height}px</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  tpl.platform === 'Facebook' ? 'bg-blue-50 text-blue-600' :
                  tpl.platform === 'Instagram'? 'bg-pink-50 text-pink-600' : 'bg-muted text-muted-foreground'
                }`}>
                  {tpl.platform}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* How it works */}
        <div className="card-base p-5 bg-primary/5 border-primary/20">
          <h3 className="text-sm font-semibold text-primary mb-3">How it works</h3>
          <ol className="space-y-2">
            {[
              'Describe your flyer in the prompt box',
              'Choose a canvas size for your platform',
              'AI generates a professional flyer image',
              'Fine-tune it in the canvas editor',
              'Export as PNG or JPG',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <span className="w-4 h-4 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
